import { ChecklistModuleDefinition } from '../../types/due-diligence';

export const waterFeaturesModule: ChecklistModuleDefinition = {
  code: 'water_features',
  name: 'Water Features',
  description: 'Ponds, creeks, wetlands, and springs',
  categories: [
    {
      code: 'ponds',
      name: 'Ponds',
      items: [
        {
          code: 'pond_inventory',
          title: 'Pond inventory',
          description: 'Document all ponds on property',
          isGroupTask: false,
          dataFields: [
            { key: 'pond_count', label: 'Number of Ponds', type: 'number' },
            { key: 'total_surface_acres', label: 'Total Surface Acres', type: 'number', unit: 'acres' },
          ],
        },
        {
          code: 'pond_assessment',
          title: 'Pond condition assessment',
          description: 'Evaluate pond condition',
          isGroupTask: false,
          dataFields: [
            { key: 'pond_type', label: 'Pond Type', type: 'select', options: [
              { value: 'farm_pond', label: 'Farm Pond' },
              { value: 'spring_fed', label: 'Spring Fed' },
              { value: 'stream_fed', label: 'Stream Fed' },
              { value: 'natural', label: 'Natural Pond' },
            ]},
            { key: 'surface_area', label: 'Surface Area', type: 'number', unit: 'acres' },
            { key: 'estimated_depth', label: 'Estimated Max Depth', type: 'number', unit: 'feet' },
            { key: 'dam_condition', label: 'Dam Condition', type: 'select', options: [
              { value: 'excellent', label: 'Excellent' },
              { value: 'good', label: 'Good' },
              { value: 'fair', label: 'Fair' },
              { value: 'poor', label: 'Poor' },
              { value: 'na', label: 'N/A - No Dam' },
            ]},
            { key: 'spillway_condition', label: 'Spillway Condition', type: 'string' },
            { key: 'water_quality', label: 'Water Quality', type: 'string' },
          ],
          suggestedFlags: [
            { flag: 'big_green', condition: 'Quality pond, asset to property' },
            { flag: 'yellow', condition: 'Pond needs maintenance' },
            { flag: 'red', condition: 'Dam in poor condition' },
          ],
        },
        {
          code: 'pond_stocking',
          title: 'Fish stocking/population',
          description: 'Pond fish population assessment',
          isGroupTask: false,
          dataFields: [
            { key: 'stocked', label: 'Pond Stocked', type: 'boolean' },
            { key: 'fish_species', label: 'Fish Species', type: 'array' },
            { key: 'fishing_quality', label: 'Fishing Quality', type: 'select', options: [
              { value: 'excellent', label: 'Excellent' },
              { value: 'good', label: 'Good' },
              { value: 'fair', label: 'Fair' },
              { value: 'poor', label: 'Poor' },
            ]},
            { key: 'management_notes', label: 'Management Notes', type: 'string' },
          ],
          suggestedFlags: [
            { flag: 'big_green', condition: 'Excellent fishing pond' },
          ],
        },
        {
          code: 'pond_uses',
          title: 'Pond use potential',
          description: 'Potential uses for pond',
          isGroupTask: false,
          dataFields: [
            { key: 'uses', label: 'Potential Uses', type: 'multiselect', options: [
              { value: 'fishing', label: 'Fishing' },
              { value: 'swimming', label: 'Swimming' },
              { value: 'livestock', label: 'Livestock Water' },
              { value: 'irrigation', label: 'Irrigation' },
              { value: 'fire_protection', label: 'Fire Protection' },
              { value: 'recreation', label: 'Recreation/Boating' },
              { value: 'waterfowl', label: 'Waterfowl Habitat' },
            ]},
          ],
        },
      ],
    },
    {
      code: 'streams_creeks',
      name: 'Streams & Creeks',
      items: [
        {
          code: 'stream_inventory',
          title: 'Stream/creek inventory',
          description: 'Document water bodies on property',
          isGroupTask: false,
          dataFields: [
            { key: 'stream_count', label: 'Number of Streams/Creeks', type: 'number' },
            { key: 'total_frontage', label: 'Total Frontage', type: 'number', unit: 'feet' },
            { key: 'named_streams', label: 'Named Streams', type: 'array' },
          ],
        },
        {
          code: 'stream_characteristics',
          title: 'Stream characteristics',
          description: 'Assess stream quality and type',
          isGroupTask: false,
          dataFields: [
            { key: 'stream_type', label: 'Stream Type', type: 'select', options: [
              { value: 'perennial', label: 'Perennial (Year-round)' },
              { value: 'intermittent', label: 'Intermittent' },
              { value: 'ephemeral', label: 'Ephemeral' },
            ]},
            { key: 'average_width', label: 'Average Width', type: 'number', unit: 'feet' },
            { key: 'water_quality', label: 'Water Quality', type: 'string' },
            { key: 'stream_health', label: 'Stream Health', type: 'select', options: [
              { value: 'excellent', label: 'Excellent' },
              { value: 'good', label: 'Good' },
              { value: 'fair', label: 'Fair' },
              { value: 'poor', label: 'Poor' },
            ]},
            { key: 'fish_present', label: 'Fish Present', type: 'boolean' },
          ],
          suggestedFlags: [
            { flag: 'big_green', condition: 'Quality perennial stream' },
          ],
        },
        {
          code: 'stream_access',
          title: 'Stream access/restrictions',
          description: 'Access rights and limitations',
          isGroupTask: false,
          dataFields: [
            { key: 'navigable_water', label: 'Navigable Water', type: 'boolean' },
            { key: 'public_access_rights', label: 'Public Access Rights', type: 'boolean' },
            { key: 'buffer_requirements', label: 'Buffer Requirements', type: 'number', unit: 'feet' },
            { key: 'restrictions', label: 'Restrictions', type: 'array' },
          ],
          suggestedFlags: [
            { flag: 'yellow', condition: 'Public access rights exist' },
          ],
        },
      ],
    },
    {
      code: 'springs',
      name: 'Springs',
      items: [
        {
          code: 'spring_inventory',
          title: 'Spring inventory',
          description: 'Document springs on property',
          isGroupTask: false,
          dataFields: [
            { key: 'spring_count', label: 'Number of Springs', type: 'number' },
            { key: 'developed_springs', label: 'Developed Springs', type: 'number' },
          ],
        },
        {
          code: 'spring_assessment',
          title: 'Spring assessment',
          description: 'Evaluate spring quality and flow',
          isGroupTask: false,
          dataFields: [
            { key: 'flow_rate', label: 'Flow Rate', type: 'number', unit: 'gpm' },
            { key: 'seasonal_variation', label: 'Seasonal Variation', type: 'string' },
            { key: 'water_quality', label: 'Water Quality', type: 'string' },
            { key: 'development_status', label: 'Development Status', type: 'select', options: [
              { value: 'developed', label: 'Developed/Captured' },
              { value: 'partially', label: 'Partially Developed' },
              { value: 'undeveloped', label: 'Undeveloped' },
            ]},
            { key: 'potable', label: 'Potable Water Source', type: 'boolean' },
          ],
          suggestedFlags: [
            { flag: 'big_green', condition: 'Good spring, reliable flow' },
          ],
        },
      ],
    },
    {
      code: 'wetlands',
      name: 'Wetlands',
      items: [
        {
          code: 'wetland_identification',
          title: 'Wetland identification',
          description: 'Identify wetland areas',
          isGroupTask: false,
          dataFields: [
            { key: 'wetlands_present', label: 'Wetlands Present', type: 'boolean' },
            { key: 'wetland_acres', label: 'Wetland Acres', type: 'number', unit: 'acres' },
            { key: 'wetland_types', label: 'Wetland Types', type: 'array' },
            { key: 'delineation_done', label: 'Wetland Delineation Done', type: 'boolean' },
          ],
        },
        {
          code: 'wetland_restrictions',
          title: 'Wetland restrictions',
          description: 'Regulatory restrictions on wetlands',
          isGroupTask: false,
          dataFields: [
            { key: 'jurisdictional_wetlands', label: 'Jurisdictional Wetlands', type: 'boolean' },
            { key: 'buffer_requirements', label: 'Buffer Requirements', type: 'number', unit: 'feet' },
            { key: 'development_restrictions', label: 'Development Restrictions', type: 'string' },
            { key: 'permit_requirements', label: 'Permit Requirements', type: 'string' },
          ],
          suggestedFlags: [
            { flag: 'big_red', condition: 'Wetlands significantly limit use' },
            { flag: 'yellow', condition: 'Wetland restrictions apply' },
          ],
        },
        {
          code: 'wetland_value',
          title: 'Wetland ecosystem value',
          description: 'Wildlife and ecosystem benefits',
          isGroupTask: false,
          dataFields: [
            { key: 'wildlife_value', label: 'Wildlife Value', type: 'select', options: [
              { value: 'high', label: 'High' },
              { value: 'medium', label: 'Medium' },
              { value: 'low', label: 'Low' },
            ]},
            { key: 'waterfowl_use', label: 'Waterfowl Use', type: 'boolean' },
            { key: 'hunting_value', label: 'Hunting Value', type: 'string' },
          ],
          suggestedFlags: [
            { flag: 'big_green', condition: 'High-value wetland habitat' },
          ],
        },
      ],
    },
  ],
};

